import { api, type AiImageInfo, type MadeFlow, type RecentFlow } from '../sync/api';
import {
  checkFlow, checkFlowRequest, flowCopyText, flowLengthError, flowShotCount, FLOW_MODE_TH, narratorText,
  type FlowRequest, type FlowResult
} from '../video/flowPrompt';
import { $ } from './dom';
import { isOpen, openSheet } from './sheets';
import { toast } from './toast';

/*
 * เขียน prompt สำหรับ Google Flow (0025, choices 27 ต.ค. 2569 07:14: ข "Gemini ในระบบคิดให้" · ไทย/อังกฤษเลือกได้ ·
 * เฉพาะคนที่มีสิทธิ์ "รูป AI" · รอบ 1 = prompt ทีละช็อต; รอบ 2 = สร้างโครงงานวิดีโอจากชุดนี้).
 * เพิ่มเติม → "เขียน prompt สำหรับ Google Flow (AI)". The person writes what they want in plain words (and, if they like,
 * the facts that must be shown correctly); the server asks Gemini and checks the answer (src/video/flowPrompt.ts); the app
 * checks Gemini's own answer again with the same checker (BUG-13/72) and shows one card per shot with a copy button.
 * A copy that the browser refuses shows the text to copy by hand — never a silent failure. "ล่าสุด": the sets of the last
 * 24 h, opened again without paying twice (a set made while this panel was closed is collected by the server, 0024).
 */
export interface FlowSheetHooks { canUse: () => boolean; online: () => string | null }

const usd = (n: number) => n < 1 ? n.toFixed(3) : n.toFixed(2);
const errText = (e: unknown) => e instanceof Error ? e.message : String(e);
const timeOf = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)} ${iso.slice(11, 16)} น.`;

export function initFlowSheet(hooks: FlowSheetHooks) {
  const brief = $<HTMLTextAreaElement>('flBrief'), facts = $<HTMLTextAreaElement>('flFacts');
  const lang = $<HTMLSelectElement>('flLang'), aspect = $<HTMLSelectElement>('flAspect'), total = $<HTMLInputElement>('flTotal'), shot = $<HTMLSelectElement>('flShot');
  const go = $<HTMLButtonElement>('flGo');
  // 0026: ให้ Flow พากย์ด้วย + the narrator
  const narrate = $<HTMLInputElement>('flNarrate'), vSex = $<HTMLSelectElement>('flVSex'), vAge = $<HTMLSelectElement>('flVAge'), vTone = $<HTMLSelectElement>('flVTone'), vExtra = $<HTMLInputElement>('flVExtra');
  let info: AiImageInfo | null = null, infoError = '';
  let busy = false, started = 0, timer = 0;
  /** the set on show: what the copy buttons copy */
  let shown: { res: FlowResult; req: FlowRequest } | null = null;

  const say = (text: string, bad = true) => { const m = $('flMsg'); m.textContent = text; m.hidden = !text; m.classList.toggle('ok', !bad); };

  /** what is asked for now; the reason when it cannot be asked (the server checks the same, src/video/flowPrompt.ts) */
  const request = () => checkFlowRequest({ brief: brief.value, facts: facts.value, lang: lang.value, aspect: aspect.value, total: Number(total.value), shot: Number(shot.value),
    voice: narrate.checked ? { sex: vSex.value, age: vAge.value, tone: vTone.value, extra: vExtra.value } : null });

  function paint() {
    const off = !hooks.canUse() ? 'บัญชีนี้ยังไม่มีสิทธิ์ "รูป AI" ขอให้ผู้ดูแลเปิดให้ที่ หลังบ้านผู้ดูแล → ผู้ใช้'
      : !info ? (infoError || 'กำลังโหลดข้อมูล AI…')
        : info.problem ? info.problem
          : !info.flow ? 'เซิร์ฟเวอร์ยังเป็นรุ่นเก่าที่ไม่มีตัวเขียน prompt สำหรับ Flow (รอ deploy เสร็จแล้วโหลดหน้าใหม่)'
            : info.capUsd === 0 ? 'AI ของกลุ่มปิดอยู่ (เพดานต่อเดือน 0 ดอลลาร์) ผู้ดูแลตั้งเพดานได้ที่แผง "สร้างด้วย AI" ของช่องใส่รูป'
              : null;
    const prob = $('flProb');
    prob.textContent = off ?? ''; prob.hidden = !off || (!info && !infoError && hooks.canUse());
    $('flUse').textContent = info ? `เดือนนี้ทั้งกลุ่มใช้ไป $${usd(info.usedUsd)} จากเพดาน $${usd(info.capUsd)} (เพดานเดียวกับรูป AI · เริ่มใหม่ต้นเดือนตามเวลาไทย)` : (infoError || 'กำลังโหลด…');
    const t = Number(total.value), s = Number(shot.value);
    // the same checker as the server: a length it would refuse is said here, never shown as a shot count
    const lengthError = flowLengthError(t, s);
    $('flCount').textContent = lengthError || `จะได้ ${flowShotCount(t, s)} ช็อต ช็อตละราว ${s} วินาที (ตั้งความยาวต่อช็อตให้ตรงกับที่ Flow ของคุณเลือกได้)`;
    $('flCount').classList.toggle('bad', !!lengthError);
    $('flCost').textContent = info?.flow ? `prompt 1 ชุด กันเงินไว้ไม่เกิน $${usd(info.flow.maxUsd)} (คิดจริงตามที่ Google นับ มักน้อยกว่ามาก)` : '';
    if (!busy) go.textContent = shown ? 'เขียนชุดใหม่' : 'เขียน prompt';
    go.disabled = busy || !!off;
    $<HTMLButtonElement>('flCopyAll').disabled = busy;
    $('flVoiceBox').hidden = !narrate.checked;
  }

  async function loadInfo() {
    try { info = await api.aiImage.info(); infoError = ''; }
    catch (e) { info = null; infoError = 'โหลดข้อมูล AI ไม่สำเร็จ: ' + errText(e); }
    paint();
  }

  function open() {
    const no = hooks.online();
    if (no) { toast(`เขียน prompt ด้วย AI ต้องออนไลน์ (${no})`, true); return; }
    if (!busy && !shown) say('');
    paint();
    openSheet('shFlow');
    void loadInfo();
    void loadRecent();
  }

  /* ---------- copy (with a by-hand fallback) ---------- */

  async function copy(text: string, what: string) {
    const box = $<HTMLTextAreaElement>('flCopyBox');
    try {
      await navigator.clipboard.writeText(text);
      box.hidden = true;
      toast(`คัดลอก${what}แล้ว วางใน Google Flow ได้เลย`);
    } catch (e) {
      box.value = text; box.hidden = false; box.focus(); box.select();
      say(`คัดลอกอัตโนมัติไม่ได้ (${errText(e)}) — ข้อความอยู่ในช่องด้านล่าง กดค้าง เลือกทั้งหมด แล้วคัดลอกเอง`);
    }
  }

  /* ---------- make / show ---------- */

  async function make() {
    if (busy) return;
    const r = request();
    if (!r.ok) { say(r.error); if (!brief.value.trim()) brief.focus(); return; }
    busy = true; say('');
    started = Date.now();
    const tick = () => { go.textContent = `AI กำลังเขียน… ${Math.round((Date.now() - started) / 1000)} วิ`; };
    tick(); timer = window.setInterval(tick, 1000);
    paint();
    try {
      const res = await api.aiImage.flow(r.value);
      if (info) { info.usedUsd = res.usedUsd; info.capUsd = res.capUsd; }
      present(res.flow);
    } catch (e) {
      say('เขียน prompt ไม่สำเร็จ: ' + errText(e));
      if (!isOpen('shFlow')) toast('เขียน prompt สำหรับ Flow ไม่สำเร็จ: ' + errText(e), true);
      void loadInfo();
    } finally {
      busy = false; window.clearInterval(timer);
      paint();
      void loadRecent();
    }
  }

  /** a finished set → checked with the same checker as the server (BUG-13) → one card per shot */
  function present(made: MadeFlow) {
    const c = checkFlow(made.raw, made.request);
    if (!c.ok) { say(`ชุด prompt ที่ได้ใช้ไม่ได้: ${c.error} (คิดเงิน $${usd(made.costUsd)} แล้ว)`); return; }
    shown = { res: c.value, req: made.request };
    const res = c.value, req = made.request;
    $('flTitle').textContent = `${res.title} · ${res.shots.length} ช็อต · ${req.aspect} · prompt ภาษา${req.lang === 'th' ? 'ไทย' : 'อังกฤษ'}`
      + (req.voice ? ` · Flow พากย์ด้วย (${narratorText(req.voice, 'th')})` : '') + ` · คิดเงิน $${usd(made.costUsd)}`;
    $('flStyle').textContent = res.style || '(AI ไม่ได้ส่งชุดสไตล์กลางมา)';
    const box = $('flShots');
    box.textContent = '';
    res.shots.forEach((s, i) => {
      const card = document.createElement('div');
      card.className = 'flshot';
      const text = flowCopyText(res, s, req);
      const btn = Object.assign(document.createElement('button'), { className: 'btn main', type: 'button', textContent: `คัดลอกช็อตที่ ${i + 1}` });
      btn.onclick = () => void copy(text, `ช็อตที่ ${i + 1} `);
      const row = Object.assign(document.createElement('div'), { className: 'addrow' });
      row.append(btn);
      card.append(
        Object.assign(document.createElement('b'), { textContent: `ช็อตที่ ${i + 1} · ${s.seconds} วิ · ${FLOW_MODE_TH[s.mode]}` }),
        Object.assign(document.createElement('small'), { textContent: s.purpose }),
        Object.assign(document.createElement('pre'), { textContent: text }),
        ...(s.modeNote ? [Object.assign(document.createElement('small'), { textContent: `แนบใน Flow: ${s.modeNote}` })] : []),
        ...(s.onScreen ? [Object.assign(document.createElement('small'), { textContent: `ข้อความบนจอ (ใส่ใน CraftStudio): ${s.onScreen}` })] : []),
        ...(s.voice ? [Object.assign(document.createElement('small'), { textContent: req.voice ? `บทพากย์ (อยู่ใน prompt แล้ว): ${s.voice}` : `บทพากย์: ${s.voice}` })] : []),
        row);
      box.append(card);
    });
    const tips = $('flTips');
    tips.textContent = '';
    for (const t of res.tips.length ? res.tips : ['(AI ไม่ได้ส่งเคล็ดลับมา)']) tips.append(Object.assign(document.createElement('li'), { textContent: t }));
    $('flOut').hidden = false;
    const notes = [...(made.warning ? [made.warning] : []), ...res.notes];
    say(notes.length ? `ได้ prompt แล้ว มีเรื่องที่ควรรู้: ${notes.join(' · ')}` : 'ได้ prompt แล้ว กดคัดลอกทีละช็อตไปวางใน Google Flow', notes.length > 0);
    if (!isOpen('shFlow')) toast('เขียน prompt สำหรับ Flow เสร็จแล้ว เปิด เพิ่มเติม → "เขียน prompt สำหรับ Google Flow (AI)"');
  }

  function copyAll() {
    if (!shown) return;
    const { res, req } = shown;
    void copy(res.shots.map((s, i) => `=== ช็อตที่ ${i + 1} (${s.seconds} วิ) ===\n${flowCopyText(res, s, req)}`).join('\n\n'), 'ทุกช็อต');
  }

  /* ---------- ล่าสุด (24 h) ---------- */

  let recentKey = '';
  async function loadRecent() {
    const msg = $('flRecentMsg');
    $('flRecent').hidden = false;
    msg.textContent = 'กำลังเช็กงานที่ค้างอยู่…';
    let list: RecentFlow[];
    try {
      const r = await api.aiImage.recent();
      if (!r.flows) { msg.textContent = 'เซิร์ฟเวอร์ยังเป็นรุ่นเก่า (รอ deploy เสร็จแล้วโหลดหน้าใหม่)'; return; }
      list = r.flows;
      if (info) { info.usedUsd = r.usedUsd; info.capUsd = r.capUsd; paint(); }
    } catch (e) { msg.textContent = 'โหลดรายการล่าสุดไม่สำเร็จ: ' + errText(e); return; }
    msg.textContent = list.length ? 'แตะชุดที่เสร็จแล้วเพื่อเปิดอีกครั้ง (ไม่คิดเงินซ้ำ)' : 'ยังไม่มี prompt Flow ใน 24 ชั่วโมงที่ผ่านมา';
    const key = JSON.stringify(list.map(l => [l.id, l.status]));
    if (key === recentKey) return;   // the same rows: leave the buttons as they are (CORE: never rebuild what may be touched)
    recentKey = key;
    const box = $('flRecentList');
    box.textContent = '';
    for (const l of list) {
      const b = document.createElement('button');
      b.className = 'airow'; b.type = 'button'; b.dataset.status = l.status;
      const state = l.status === 'done' ? `${l.aspect} · ${l.total} วิ · $${usd(l.costUsd)}` : l.status === 'making' ? 'กำลังเขียน…' : 'ไม่สำเร็จ (แตะดูเหตุผล)';
      b.append(Object.assign(document.createElement('b'), { textContent: l.status === 'done' ? (l.title ?? l.brief) : l.brief }),
        Object.assign(document.createElement('small'), { textContent: `${timeOf(l.createdAt)} · ${state}` }));
      b.onclick = () => void openRecent(l);
      box.append(b);
    }
  }
  async function openRecent(l: RecentFlow) {
    if (busy) { say('กำลังทำงานอื่นอยู่ รอให้เสร็จก่อน'); return; }
    if (l.status === 'failed') { say(`ชุดนี้ไม่สำเร็จ: ${l.error ?? 'ไม่ทราบเหตุผล'}`); return; }
    if (l.status === 'making') { say('ชุดนี้ AI ยังเขียนไม่เสร็จ ลองเปิดแผงนี้อีกครั้งภายหลัง (ไม่ต้องสร้างใหม่)', false); void loadRecent(); return; }
    busy = true; say(''); paint();
    try { present((await api.aiImage.flowJob(l.id)).flow); }
    catch (e) { say('เปิดชุดนี้ไม่สำเร็จ: ' + errText(e)); }
    finally { busy = false; paint(); }
  }

  go.onclick = () => void make();
  $('flCopyAll').onclick = copyAll;
  for (const el of [lang, aspect, shot, narrate]) el.onchange = paint;
  total.oninput = paint;
  $('dFlow').onclick = open;
  /** the row in เพิ่มเติม: accounts that may use it (the server checks too) */
  const showRow = () => { $('dFlowRow').hidden = !hooks.canUse(); };
  return { open, showRow };
}
